import { buildRailWorkspaceMoves } from "@/dnd";
import { classifyTransfer } from "@/dnd/classifyTransfer";
import {
  asDraggableData,
  destinationFor,
  droppableId,
  type DroppableData,
} from "@/dnd/ids";
import { cnTransferDropTargetSurface } from "@/dnd/transferDropTargetStyles";
import { useRailOsDropHighlightId } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/RailFilesDropHighlight";
import { RailOsFileDropZone } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/RailOsFileDropZone";
import type { FilesRailOsDropHandler } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/useFilesRailWorkspaceFiles";
import { cn } from "@/utils";
import { useDndContext, useDroppable } from "@dnd-kit/core";
import { useCallback, useMemo } from "react";

/**
 * Combined OS-file + in-app move/copy drop target for the files rail.
 * Workspace-scoped payloads move (including cross-workspace); canvas /
 * flow-session payloads copy into the destination folder.
 */
export function FilesRailDropTarget({
  workspaceId,
  parentPath,
  onOsDrop,
  children,
  className,
}: {
  workspaceId: string;
  /** Destination folder (`""` = workspace root). */
  parentPath: string;
  onOsDrop?: FilesRailOsDropHandler | null;
  children: React.ReactNode;
  className?: string;
}) {
  const dropId = droppableId.railWorkspaceFolder(workspaceId, parentPath);
  const osActiveDropId = useRailOsDropHighlightId();
  const { active, over } = useDndContext();

  const isActiveDropTarget = active
    ? over?.id === dropId
    : osActiveDropId != null && osActiveDropId === String(dropId);

  const droppableData = useMemo((): DroppableData => {
    return {
      surface: "rail-workspace-folder",
      // filesOnly: this is a files organizing surface, so a canvas/chat dropped
      // here is rejected ("can't move here") rather than moved to the workspace.
      destination: {
        ...destinationFor("workspace-folder", { workspaceId, parentPath }),
        filesOnly: true,
      },
    };
  }, [workspaceId, parentPath]);

  const droppable = useDroppable({
    id: dropId,
    data: droppableData,
  });

  const draggable = active ? asDraggableData(active.data.current) : null;

  // Shared-with-me files copy into the workspace (any class), so they bypass the
  // owned-file move-batch validation below and are always a valid drop target.
  const allShared =
    draggable?.kind === "files" &&
    draggable.payloads.length > 0 &&
    draggable.payloads.every((p) => p.sharedSource);

  const allWorkspacePayloads =
    draggable?.kind === "files" &&
    draggable.payloads.every((p) => p.sourceClass === "workspace");

  const crossClassCopyPayloads =
    draggable?.kind === "files" &&
    draggable.payloads.length > 0 &&
    draggable.payloads.every(
      (p) => p.sourceClass === "canvas" || p.sourceClass === "flow-session",
    );

  const railMoveBatch =
    isActiveDropTarget && allWorkspacePayloads && !allShared
      ? buildRailWorkspaceMoves(draggable.payloads, {
          workspaceId,
          parentPath,
        })
      : null;

  const dropInvalid =
    isActiveDropTarget &&
    draggable?.kind === "files" &&
    !crossClassCopyPayloads &&
    !allShared &&
    (railMoveBatch?.rejections.some((r) => r.kind === "self_folder") ||
      (railMoveBatch?.moves.length === 0 &&
        (railMoveBatch?.rejections.length ?? 0) > 0));

  // Section drags (from the active-context rail) copy into a folder named
  // after the section — they should light the same drop highlight as files.
  // `section_to_folder` is the only valid section classification here; an
  // empty section classifies as `noop` and stays unhighlighted.
  const sectionDropValid =
    draggable?.kind === "section" &&
    classifyTransfer(draggable, droppableData.destination).kind ===
      "section_to_folder";

  const showOsHighlight = isActiveDropTarget && !active && !!onOsDrop;

  // Canvases/chats can't be filed into a files surface — show the grayed
  // "can't" ring (the classifier returns invalid, so a drop is a no-op).
  const nonFileInvalid =
    isActiveDropTarget &&
    (draggable?.kind === "canvas-card" || draggable?.kind === "chat-session");

  const showDndHighlight =
    isActiveDropTarget &&
    !!active &&
    ((draggable?.kind === "files" && !dropInvalid) || sectionDropValid);

  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      droppable.setNodeRef(el);
    },
    [droppable],
  );

  const showRustHighlight = showOsHighlight || showDndHighlight;

  return (
    <RailOsFileDropZone
      ref={setRef}
      dropId={dropId}
      onDrop={onOsDrop ?? null}
      className={cn(
        cnTransferDropTargetSurface({
          isOver: showRustHighlight || dropInvalid || nonFileInvalid,
          disallowed: dropInvalid || nonFileInvalid,
        }),
        className,
      )}
    >
      {children}
    </RailOsFileDropZone>
  );
}
